// REST client — envelope-aware ({success, data, error, meta}).
// The API base is same-origin under /api/v1 (dev serves the shell at /app/).
// Authenticated calls ride the dev session header in development; production
// swaps in the operator bearer token here and nowhere else.

const BASE = '/api/v1';
const SESSION_KEY = 'hw.devPlayer';

export class ApiError extends Error {
  constructor(code, message, status) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

function authHeaders() {
  try {
    const player = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    if (player?.playerId) return { 'X-Dev-Player': String(player.playerId) };
  } catch { /* no session */ }
  return {};
}

async function request(path, options = {}) {
  const response = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(options.headers || {}) },
    ...options,
  });
  let body = null;
  try { body = await response.json(); } catch { /* non-json */ }
  if (!body || body.success !== true) {
    const error = body?.error || {};
    throw new ApiError(error.code || `HTTP_${response.status}`, error.message || response.statusText, response.status);
  }
  return body.data;
}

const post = (path, payload) => request(path, { method: 'POST', body: JSON.stringify(payload ?? {}) });

export const api = {
  settings: () => request('/public/settings'),

  // --- races / fairness (public) ---
  upcoming: (limit = 12) => request(`/races/upcoming?limit=${limit}`),
  results: (limit = 12) => request(`/races/results?limit=${limit}`),
  racecard: (raceId) => request(`/races/${raceId}/racecard`),
  playback: (raceId) => request(`/races/${raceId}/playback`),
  result: (raceId) => request(`/races/${raceId}/result`),
  proof: (raceId) => request(`/races/${raceId}/proof`),
  epochCommitment: (ordinal) => request(`/epochs/${ordinal}/commitment`),
  epochRoster: (ordinal) => request(`/epochs/${ordinal}/roster`),

  // --- stats (public) ---
  horses: (params = '') => request(`/horses${params ? `?${params}` : ''}`),
  horse: (horseId) => request(`/horses/${horseId}`),
  standings: (season) => request(`/standings/season/${season}`),
  records: () => request('/records'),
  tracks: () => request('/tracks'),

  // --- session / wallet ---
  devLogin: (name) => post('/dev/login', { name }),
  balance: () => request('/wallet/balance'),

  // --- betting ---
  markets: (raceId) => request(`/betting/markets/${raceId}`),
  priceCoupon: (legs) => post('/betting/price', { legs }),
  placeCoupon: (payload) => post('/betting/coupons', payload),
  myCoupons: () => request('/betting/my'),

  // --- packs / stable / buyback ---
  packSeries: () => request('/packs/series'),
  buyPack: (code) => post(`/packs/${code}/purchase`),
  myStable: () => request('/stable'),
  buybackQuote: (shareId) => post('/buyback/quote', { shareId }),
  buybackExecute: (offerId) => post('/buyback/execute', { offerId }),

  // --- prizes / jackpot ---
  prizePeriods: () => request('/prizes/periods'),
  myAwards: () => request('/prizes/awards/my'),
  jackpot: () => request('/jackpot'),
};
